import { useEffect, useMemo, useState } from 'react'
import { useDevice } from '../hooks/useDevice'
import { useSwitchListSubscription } from '../hooks/useSwitchListSubscription'
import { recomputeSwitchList, updateWorkOrderStatus } from '../api/workorders'
import { SwitchListPrintSheet } from './SwitchListPrintSheet'
import {
  buildSwitchListPrintViewModel,
  type SwitchListPrintViewModel,
} from '../print/switchListPrintViewModel'
import { formatEnteredTrackFeet } from '../utils/formatEnteredTrackFeet'
import type { MicroStep, SwitchList, SwitchListItem } from '../types/interchange'

export interface SwitchListDrawerProps {
  isOpen: boolean
  onClose: () => void
  workOrderId: string
  switchList: SwitchList
  woNumber?: string
  swlNumber?: string
  sourceTrackName?: string
  targetTrackName?: string
  isConnected?: boolean
  subscribe?: (callback: (message: any) => void) => () => void
  onStatusChange?: (workOrderId: string, newStatus: string) => void
}

function stepClass(type: string) {
  switch (type) {
    case 'THROW_SWITCH':
      return 'bg-amber-100 text-amber-800'
    case 'TRAVEL':
      return 'bg-blue-100 text-blue-800'
    case 'COUPLE':
      return 'bg-green-100 text-green-800'
    case 'UNCOUPLE':
      return 'bg-red-100 text-red-800'
    case 'REVERSE':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function StepBadge({ step }: { step: MicroStep }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${stepClass(step.type)}`}>
      {step.type}
    </span>
  )
}

export function SwitchListDrawer({
  isOpen,
  onClose,
  workOrderId,
  switchList,
  woNumber,
  swlNumber,
  sourceTrackName = 'Source',
  targetTrackName = 'Target',
  isConnected = false,
  subscribe,
  onStatusChange
}: SwitchListDrawerProps) {
  const { isMobile, isTablet } = useDevice()
  const isTouch = isMobile || isTablet
  const [isRecomputing, setIsRecomputing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [manualSwitchList, setManualSwitchList] = useState<SwitchList | null>(null)
  const [manualNotice, setManualNotice] = useState<string | null>(null)
  const [printModel, setPrintModel] = useState<SwitchListPrintViewModel | null>(null)

  const { recomputeNotice } = useSwitchListSubscription(
    workOrderId,
    subscribe ?? (() => () => {}),
    setManualSwitchList
  )

  useEffect(() => {
    setManualSwitchList(null)
    setManualNotice(null)
  }, [workOrderId, switchList])

  const displayList = useMemo(() => {
    return manualSwitchList ?? switchList
  }, [manualSwitchList, switchList])

  const displayWoNumber = displayList.woNumber ?? woNumber ?? workOrderId
  const displaySwlNumber = displayList.swlNumber ?? swlNumber

  const notice = manualNotice ?? recomputeNotice

  if (!isOpen) {
    return null
  }

  const handleRecompute = async () => {
    setIsRecomputing(true)
    try {
      const updated = await recomputeSwitchList(workOrderId)
      setManualSwitchList(updated)
      setManualNotice('Switch list manually recomputed.')
      setTimeout(() => setManualNotice(null), 5000)
    } catch (error) {
      setManualNotice('Recompute failed. Please try again.')
      console.error('Recompute failed', error)
    } finally {
      setIsRecomputing(false)
    }
  }

  const handleStatusChange = async (status: 'HELD' | 'CANCELLED') => {
    setIsUpdating(true)
    try {
      await updateWorkOrderStatus(workOrderId, status)
      onStatusChange?.(workOrderId, status)
      setManualNotice(`Work order ${status.toLowerCase()}.`)
      setTimeout(() => {
        setManualNotice(null)
        onClose()
      }, 1500)
    } catch (error) {
      setManualNotice(`Failed to ${status.toLowerCase()} work order.`)
      console.error('Status update failed', error)
    } finally {
      setIsUpdating(false)
    }
  }

  // Shared print template (Plan 26 1c-i) — same view-model as the builder;
  // the drawer's window.print() path is retired.
  const handlePrint = () => {
    setPrintModel(
      buildSwitchListPrintViewModel(displayList, {
        workOrderId,
        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
      })
    )
  }

  const panelPosition = isTouch
    ? 'fixed inset-x-0 bottom-0 top-20 z-50 rounded-t-xl'
    : 'fixed top-0 right-0 h-full w-[480px] z-50'

  return (
    <div className="print:hidden fixed inset-0 z-40 bg-black/50" onClick={onClose}>
      <div
        className={`${panelPosition} flex flex-col bg-background shadow-2xl print:fixed print:inset-0 print:z-50 print:h-screen print:w-screen print:bg-white print:rounded-none`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b p-4 print:p-0">
          <div>
            <h2 className="text-lg font-semibold">
              {sourceTrackName} → {targetTrackName}
            </h2>
            {displayWoNumber && displaySwlNumber && (
              <p className="text-xs text-muted-foreground">
                Switch List: {displaySwlNumber} • Work Order: {displayWoNumber}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isConnected ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                <span className="h-2 w-2 rounded-full bg-green-600" />
                CONNECTED
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-gray-400" />
                OFFLINE
              </span>
            )}
            <button
              onClick={onClose}
              className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {notice && (
          <div className="bg-blue-50 p-3 text-sm text-blue-800 print:hidden">{notice}</div>
        )}

        <div className="grid grid-cols-2 gap-3 border-b p-4 sm:grid-cols-3 print:grid-cols-3">
          <Metric label="Time" value={`${displayList.estimatedTimeToCompleteMinutes ?? 0} min`} />
          <Metric label="Fuel" value={`${displayList.estimatedFuelBurnGallons ?? 0} gal`} />
          <Metric label="Throws" value={String(displayList.switchThrowCount ?? 0)} />
          <Metric label="Reversals" value={String(displayList.reversalCount ?? 0)} />
          <Metric label="Distance" value={`${displayList.totalDistanceFeet ?? 0} ft`} />
          <Metric label="Couplings" value={String(displayList.couplingCount ?? 0)} />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Task Sequence</h3>
          <div className="space-y-3">
            {displayList.items.map((item: SwitchListItem) => (
              <div
                key={item.taskId}
                className="rounded-md border bg-card p-3 shadow-sm print:border-0 print:bg-white"
              >
                <div className="mb-2 border-b pb-2">
                  <div className="font-semibold text-sm">
                    {item.passSummary ?? item.summary}
                  </div>
                </div>
                {item.microSteps.length > 0 && (
                  <ul className="mt-2 space-y-1 pl-4 text-sm text-muted-foreground print:text-black">
                    {item.microSteps.map((step, stepIndex) => (
                      <li key={stepIndex} className="flex items-start gap-2">
                        <StepBadge step={step} />
                        <span className="flex-1">{step.description ?? step.type}</span>
                        {step.switchName && (
                          <span className="text-xs text-muted-foreground">{step.switchName}</span>
                        )}
                        {step.travelDistanceFeet !== undefined && (
                          <span className="text-xs text-muted-foreground">{formatEnteredTrackFeet(step.travelDistanceFeet)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t p-4 print:hidden">
          <button
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted pointer-coarse:min-h-11"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted pointer-coarse:min-h-11"
          >
            Print
          </button>
          <button
            onClick={() => handleStatusChange('HELD')}
            disabled={isUpdating}
            className="rounded-md border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm font-medium text-yellow-900 hover:bg-yellow-100 disabled:opacity-50 pointer-coarse:min-h-11"
          >
            Hold
          </button>
          <button
            onClick={() => handleStatusChange('CANCELLED')}
            disabled={isUpdating}
            className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-100 disabled:opacity-50 pointer-coarse:min-h-11"
          >
            Abort
          </button>
          <button
            onClick={handleRecompute}
            disabled={isRecomputing}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 pointer-coarse:min-h-11"
          >
            {isRecomputing ? 'Recomputing...' : 'Recompute'}
          </button>
        </div>
      </div>
      {printModel && (
        <SwitchListPrintSheet
          viewModel={printModel}
          active
          onDone={() => setPrintModel(null)}
        />
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/40 p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  )
}
